import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Fade,
  Zoom,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  People as PeopleIcon,
  WhatsApp as WhatsAppIcon,
  Payment as PaymentIcon,
  Celebration as CelebrationIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

interface SorteadorConfig {
  id: number;
  quantidade_clientes: number;
  auto_pay_fatura: boolean;
  send_whatsapp_msg: boolean;
  aceita_inadimplente: boolean;
  qtde_dias_vencido?: number;
  tipo_cliente?: 'todos' | 'pf' | 'pj';
  escritorio_ids?: number[] | null;
}

interface ClienteSorteado {
  id: number;
  nome: string;
  email?: string;
  telefone?: string;
  whatsapp?: string;
}

interface SorteioResultado {
  clientes: ClienteSorteado[];
  config: SorteadorConfig;
  totalElegiveis: number;
}

interface SorteadorResultsProps {
  resultado: SorteioResultado;
  onPremiar: () => void;
  isLoading: boolean;
}

const CONFETTI_COLORS = ['#2f80ed', '#27ae60', '#f2c94c', '#eb5757', '#9b51e0', '#00a8a8'];

const SorteadorResults: React.FC<SorteadorResultsProps> = ({
  resultado,
  onPremiar,
  isLoading,
}) => {
  const theme = useTheme();
  const [clientesVisiveis, setClientesVisiveis] = useState<number[]>([]);
  const [showPremiarButton, setShowPremiarButton] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [telefonesVisiveis, setTelefonesVisiveis] = useState<Record<number, boolean>>({});

  const confettiPieces = useMemo(() => {
    return Array.from({ length: 90 }, (_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
      size: 6 + Math.random() * 8,
      delay: Math.random() * 0.8,
      duration: 2.4 + Math.random() * 1.5,
      rotation: Math.random() * 360,
      drift: (Math.random() - 0.5) * 220,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
    }));
  }, [resultado.clientes]);

  useEffect(() => {
    setShowConfetti(false);
    const timer = setTimeout(() => setShowConfetti(false), 4200);

    return () => clearTimeout(timer);
  }, [resultado.clientes]);

  useEffect(() => {
    // Efeito de aparecimento sequencial dos clientes
    const timer = setTimeout(() => {
      let index = 0;
      const interval = setInterval(() => {
        if (index < resultado.clientes.length) {
          setClientesVisiveis(prev => [...prev, index]);
          index++;
        } else {
          clearInterval(interval);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 4200);
          // Mostrar botão de premiar após todos os clientes aparecerem
          setTimeout(() => setShowPremiarButton(true), 1000);
        }
      }, 800); // Delay de 800ms entre cada cliente

      return () => clearInterval(interval);
    }, 500);

    return () => clearTimeout(timer);
  }, [resultado.clientes.length]);

  const getInitials = (nome: string): string => {
    return nome
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (nome: string): string => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ];
    
    const index = nome.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const toggleTelefones = (clienteId: number) => {
    setTelefonesVisiveis(prev => ({
      ...prev,
      [clienteId]: !prev[clienteId],
    }));
  };

  return (
    <Paper
      sx={{
        p: 4,
        borderRadius: 3,
        '@keyframes sorteadorConfettiFall': {
          '0%': {
            opacity: 1,
            transform: 'translate3d(0, -12vh, 0) rotate(0deg)',
          },
          '80%': {
            opacity: 1,
          },
          '100%': {
            opacity: 0,
            transform: 'translate3d(var(--confetti-drift), 108vh, 0) rotate(760deg)',
          },
        },
      }}
    >
      {showConfetti && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: (theme) => theme.zIndex.modal + 1,
          }}
        >
          {confettiPieces.map((piece) => (
            <Box
              key={piece.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: piece.left,
                width: piece.size,
                height: piece.size * 1.6,
                bgcolor: piece.color,
                borderRadius: piece.borderRadius,
                opacity: 0.95,
                transform: `rotate(${piece.rotation}deg)`,
                animation: `sorteadorConfettiFall ${piece.duration}s ease-out ${piece.delay}s forwards`,
                '--confetti-drift': `${piece.drift}px`,
              }}
            />
          ))}
        </Box>
      )}

      {/* Header dos resultados */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        
        
        <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'primary.main' }}>
          🎉 Sorteio Realizado com Sucesso! 🎉
        </Typography>
        
        <Typography variant="h6" color="text.secondary" gutterBottom>
        🏆 {resultado.clientes.length} cliente(s) sorteado(s)
        </Typography>
      </Box>

      {/* Lista de clientes sorteados */}
      <Box sx={{ mb: 4 }}>
        
        <Grid container spacing={3} justifyContent="center">
          {resultado.clientes.map((cliente, index) => (
            <Grid item xs={12} sm={6} md={4} key={cliente.id}>
              <Fade in={clientesVisiveis.includes(index)} timeout={600}>
                <Card 
                  sx={{ 
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transform: clientesVisiveis.includes(index) ? 'scale(1)' : 'scale(0.8)',
                    transition: 'all 0.6s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          bgcolor: getRandomColor(cliente.nome),
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          border: '4px solid',
                          borderColor: 'primary.main',
                        }}
                      >
                        {getInitials(cliente.nome)}
                      </Avatar>
                      
                      {/* Badge de sorteado */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: 'warning.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: 30,
                          height: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.2rem',
                        }}
                      >
                        🎯
                      </Box>
                    </Box>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {cliente.nome}
                    </Typography>
                    
                    {cliente.email && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        📧 {cliente.email}
                      </Typography>
                    )}
                    
                    {(cliente.telefone || cliente.whatsapp) && (
                      <Box sx={{ mt: 1.5 }}>
                        <Tooltip title={telefonesVisiveis[cliente.id] ? 'Ocultar telefones' : 'Mostrar telefones'}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => toggleTelefones(cliente.id)}
                            aria-label={telefonesVisiveis[cliente.id] ? 'Ocultar telefones' : 'Mostrar telefones'}
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {telefonesVisiveis[cliente.id] && (
                          <Box sx={{ mt: 0.5 }}>
                            {cliente.telefone && (
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Telefone: {cliente.telefone}
                              </Typography>
                            )}

                            {cliente.whatsapp && (
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                WhatsApp: {cliente.whatsapp}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {false && cliente.telefone && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        📞 {cliente.telefone}
                      </Typography>
                    )}
                    
                    {false && cliente.whatsapp && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        📱 {cliente.whatsapp}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: 2 }}>
                      <Chip 
                        label="🎉 SORTEADO!" 
                        color="success" 
                        variant="filled"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Botão de premiar */}
      <Fade in={showPremiarButton} timeout={1000}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body1">
              <strong>Próximo passo:</strong> Clique no botão abaixo para aplicar as premiações automaticamente.
              {resultado.config.auto_pay_fatura && ' As mensalidades serão baixadas automaticamente.'}
              {resultado.config.send_whatsapp_msg && ' As notificações WhatsApp serão enviadas.'}
            </Typography>
          </Alert>
          
          <Button
            variant="contained"
            size="large"
            onClick={onPremiar}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <TrophyIcon />}
            sx={{
              minWidth: 300,
              height: 60,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              bgcolor: 'success.main',
              '&:hover': {
                bgcolor: 'success.dark',
              }
            }}
          >
            {isLoading ? 'Aplicando Premiações...' : '🏆 Aplicar Premiações'}
          </Button>
        </Box>
      </Fade>
    </Paper>
  );
};

export default SorteadorResults;
